<div class="h-full w-full bg-black relative flex flex-col overflow-hidden">
  <!-- Loading -->
  <div *ngIf="loading && !errorState" class="absolute inset-0 flex items-center justify-center z-50 bg-black">
    <app-thinking-orb state="working" [size]="128" class="text-zaxis-accent"></app-thinking-orb>
  </div>

  <!-- Error State -->
  <div *ngIf="errorState" class="absolute inset-0 flex flex-col items-center justify-center z-50 bg-black">
    <div class="bg-black border border-zaxis-border rounded-none p-8 max-w-md text-center">
      <div class="flex justify-center mb-4"><i class="ph ph-warning text-5xl text-yellow-500"></i></div>
      <h2 class="text-xl font-mono font-bold tracking-widest text-white uppercase mb-2">Error al cargar la sesión</h2>
      <p class="text-gray-400 text-sm mb-6">No se pudo cargar la telemetría de esta sesión o no tienes permisos suficientes.</p>
      <button (click)="goBack()" class="bg-zaxis-accent hover:bg-white text-black font-bold font-mono text-[10px] uppercase tracking-widest rounded-none shadow-pixel transition-all px-6 py-2 border border-zaxis-accent">
        Volver al Dashboard
      </button>
    </div>
  </div>

  <!-- Top Bar (overlay on 3D) -->
  <header class="absolute top-0 left-0 w-full p-4 flex justify-between items-center bg-black border-b border-zaxis-border shadow-pixel z-10 pointer-events-none">
    <div class="pointer-events-auto">
      <button (click)="goBack()" class="px-3 py-1.5 bg-transparent border border-zaxis-border hover:bg-white/5 font-mono uppercase tracking-widest rounded-none text-white transition-colors text-sm flex items-center gap-2">
        <i class="ph ph-arrow-left"></i> Sesiones
      </button>
    </div>
    <div class="text-white text-right flex items-center gap-4 pointer-events-auto">
      <div *ngIf="isLive" class="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-none border border-red-500/30">
        <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
        <span class="text-xs font-bold tracking-wider">LIVE</span>
      </div>
      <div *ngIf="session?.exception" class="flex items-center gap-2 px-3 py-1 bg-red-900/40 text-red-400 rounded-none border border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.3)]">
        <i class="ph ph-skull animate-pulse"></i>
        <span class="text-xs font-bold tracking-wider">CRASH</span>
      </div>
      <div *ngIf="isTimezoneDifferent" class="flex items-center gap-2 px-3 py-1 bg-blue-900/40 text-blue-400 rounded-none border border-blue-500/50 cursor-help relative group">
        <i class="ph ph-globe-hemisphere-west"></i>
        <span class="text-xs font-bold tracking-wider">{{ deviceTimezoneStr }}</span>
        <div class="absolute top-full mt-2 right-0 w-64 bg-black backdrop-blur border border-zaxis-border rounded-none p-3 shadow-pixel opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none text-left z-50">
          <p class="text-xs text-gray-300">Estás viendo una sesión grabada en una zona horaria diferente ({{ deviceTimezoneStr }}). Los timestamps mostrados aquí corresponden a esa zona horaria original.</p>
        </div>
      </div>
      <div>
        <h2 class="font-bold text-base">Reproducción de Sesión</h2>
        <p class="text-[10px] text-gray-400 font-mono">{{ session?.sessionId || session?.id }}</p>
      </div>
    </div>
  </header>

  <!-- 3D Canvas (PROTAGONIST — full viewport) -->
  <div class="flex-1 w-full relative overflow-hidden">
    <div #rendererContainer class="absolute inset-0"></div>
    <!-- 3D Environment Loading Indicator -->
    <div *ngIf="!(engine.sceneReady$ | async)" class="absolute inset-0 z-30 bg-black flex flex-col items-center justify-center">
       <app-thinking-orb state="working" [size]="128" class="text-zaxis-accent mb-4"></app-thinking-orb>
       <p class="text-gray-400 font-mono text-xs tracking-[0.2em] animate-pulse">CONSTRUYENDO ESCENA 3D...</p>
    </div>
  </div>

  <!-- Crash Info Panel -->
  <app-session-crash-panel [exception]="session?.exception"></app-session-crash-panel>

  <!-- Playback Controls -->
  <app-session-playback-controls
    [progress]="engine.progress"
    [currentTime]="engine.currentTime"
    [duration]="engine.duration"
    [playing]="engine.playing"
    [sceneReady]="(engine.sceneReady$ | async) ?? false"
    [pendingModelDownloads]="(engine.pendingModelDownloads$ | async) ?? 0"
    (seek)="seek($event)"
    (togglePlay)="togglePlay()">
  </app-session-playback-controls>

  <!-- Panel Toggle Buttons -->
  <app-session-panel-toolbar
    [activePanel]="activePanel"
    [showScene]="engine.showScene"
    [showGazeTrail]="engine.showGazeTrail"
    [breadcrumbCount]="session?.breadcrumbs?.length || 0"
    (panelToggled)="togglePanel($event)"
    (sceneToggled)="engine.toggleSceneVisibility()"
    (gazeToggled)="engine.toggleGazeTrail()"
    (cameraReset)="resetCamera()">
  </app-session-panel-toolbar>

  <!-- Object Heatmap Panel -->
  <app-session-object-heatmap-panel
    [objectName]="engine.selectedObjectNombre"
    [heatmapStats]="heatmapStats"
    [objectStats]="engine.selectedObjectNombre ? objectStats[engine.selectedObjectNombre] : null"
    [editModeEnabled]="engine.editModeEnabled"
    [playing]="engine.playing"
    [saving]="engine.savingRotation"
    [hitDuration]="session?.telemetry?.length ? (1 / (session!.telemetry!.length / (engine.duration || 1))) : 0"
    [transform]="{ rotX: engine.debugRotX, rotY: engine.debugRotY, rotZ: engine.debugRotZ, posX: engine.debugPosX, posY: engine.debugPosY, posZ: engine.debugPosZ }"
    (clearSelection)="clearSelection()"
    (editModeToggled)="engine.toggleEditMode()"
    (transformChange)="engine.onTransformChange($event)"
    (resetTransform)="engine.resetDebugRotation()"
    (saveTransform)="engine.saveDebugRotation(telemetryService)">
  </app-session-object-heatmap-panel>

  <!-- Performance Chart Panel -->
  <app-session-performance-chart-panel
    *ngIf="activePanel === 'performance'"
    [frames]="engine.performanceFrames"
    [currentFrame]="engine.currentPerformanceFrame"
    [currentIndex]="engine.currentPerformanceIndex"
    (closed)="activePanel = null">
  </app-session-performance-chart-panel>

  <!-- Side Panel -->
  <app-session-side-panel
    [activePanel]="activePanel"
    [sidebarMode]="sidebarMode"
    [session]="session"
    [breadcrumbs]="session?.breadcrumbs || []"
    [currentTime]="engine.currentTime"
    [currentAbsoluteTime]="engine.currentAbsoluteTime"
    [averageFps]="engine.averageFps"
    [currentRefreshRate]="engine.refreshRate"
    [currentCpuLevel]="engine.currentCpuLevel"
    [currentGpuLevel]="engine.currentGpuLevel"
    [currentPerformanceFrame]="engine.currentPerformanceFrame"
    [isLoadingNetworkRequests]="dataService.isLoadingNetworkRequests"
    [objects]="session?.sceneSnapshot?.objects || []"
    [objectStats]="objectStats"
    [uploadingModel]="dataService.uploadingModel"
    (panelClosed)="activePanel = null"
    (sidebarModeToggled)="toggleSidebarMode()"
    (viewException)="showException($event)"
    (focusObject)="focusAndSelectObject($event)"
    (linkModel)="linkModelUrl($event)"
    (uploadModel)="uploadModelFile($event.obj, $event.file)">
  </app-session-side-panel>

  <!-- Exception Modal -->
  <app-session-exception-modal
    [exception]="selectedException"
    (closed)="closeException()">
  </app-session-exception-modal>

</div>
